import { jobLegOccupancyEndMinutes } from './occupancyWindow';
import { estimateTravelKm, estimateTravelMinutes } from './travelTime';
import type {
  ChainTimelineEntry,
  ChainValidationResult,
  ScheduleLegInput,
  TravelTimeOptions,
} from './types';
import {
  formatIssuesEnglish,
  formatMinutesLabel,
  type ScheduleValidationIssue,
} from './validationIssues';

function intervalsOverlap(a0: number, a1: number, b0: number, b1: number): boolean {
  return a0 < b1 && b0 < a1;
}

function legRequiresVehicleAway(leg: ScheduleLegInput, anchor: ScheduleLegInput): boolean {
  if (leg.legType === 'standby_block') return false;
  const standbyLoc = anchor.pickup;
  return (
    leg.pickup.lat !== standbyLoc.lat ||
    leg.pickup.lng !== standbyLoc.lng ||
    leg.dropoff.lat !== standbyLoc.lat ||
    leg.dropoff.lng !== standbyLoc.lng
  );
}

/**
 * Validate a vehicle's leg chain for time-window and Stand by feasibility.
 * EXT.SCHED Phase 1 core engine.
 */
export function validateVehicleChain(
  legs: ScheduleLegInput[],
  options: TravelTimeOptions = {},
  start?: { location: ScheduleLegInput['pickup']; timeMinutes: number }
): ChainValidationResult {
  const issues: ScheduleValidationIssue[] = [];
  const timeline: ChainTimelineEntry[] = [];
  let totalDeadheadKm = 0;

  if (legs.length === 0) {
    return { ok: true, errors: [], issues: [], timeline, totalDeadheadKm: 0 };
  }

  let prevEndLoc = start?.location ?? legs[0].pickup;
  let prevEndTime = start?.timeMinutes ?? legs[0].earliestStartMinutes;
  let hasPriorLeg = start != null;

  const standbyBlocks = legs.filter((l) => l.legType === 'standby_block');
  const chainBuffer = options.chainBufferMinutes ?? 0;

  for (const leg of legs) {
    const travelMinutes = estimateTravelMinutes(prevEndLoc, leg.pickup, options);
    totalDeadheadKm += estimateTravelKm(prevEndLoc, leg.pickup, options);

    const bufferMinutes = hasPriorLeg ? chainBuffer : 0;
    const arriveMinutes = prevEndTime + travelMinutes + bufferMinutes;
    const latestStart = leg.latestStartMinutes ?? leg.earliestStartMinutes;
    const legLabel = leg.label ?? leg.legId;

    if (arriveMinutes > latestStart + 1e-6) {
      issues.push({
        code: 'ARRIVE_AFTER_LATEST',
        params: {
          leg: legLabel,
          arrive: formatMinutesLabel(arriveMinutes),
          latest: formatMinutesLabel(latestStart),
        },
      });
    }

    const serviceStart = Math.max(arriveMinutes, leg.earliestStartMinutes);
    const waitMinutes = Math.max(0, serviceStart - arriveMinutes);
    // Standby ends at absolute end_time. Transfers occupy for service_duration
    // (see jobLegOccupancyEndMinutes) starting at serviceStart.
    const departMinutes =
      leg.legType === 'standby_block' && leg.endTimeMinutes != null
        ? leg.endTimeMinutes
        : serviceStart +
          Math.max(0, jobLegOccupancyEndMinutes(leg) - leg.earliestStartMinutes);

    if (leg.legType === 'standby_block' && leg.endTimeMinutes == null) {
      issues.push({
        code: 'STANDBY_REQUIRES_END',
        params: { leg: legLabel },
      });
    }

    if (leg.legType === 'standby_block' && serviceStart > leg.earliestStartMinutes + 1e-6) {
      issues.push({
        code: 'STANDBY_MUST_START',
        params: {
          leg: legLabel,
          time: formatMinutesLabel(leg.earliestStartMinutes),
        },
      });
    }

    for (const block of standbyBlocks) {
      if (block.legId === leg.legId || block.endTimeMinutes == null) continue;
      const blockStart = block.earliestStartMinutes;
      const blockEnd = block.endTimeMinutes;
      if (
        legRequiresVehicleAway(leg, block) &&
        intervalsOverlap(serviceStart, departMinutes, blockStart, blockEnd)
      ) {
        issues.push({
          code: 'STANDBY_CONFLICT',
          params: {
            leg: legLabel,
            block: block.label ?? block.legId,
            start: formatMinutesLabel(blockStart),
            end: formatMinutesLabel(blockEnd),
          },
        });
      }
    }

    timeline.push({
      legId: leg.legId,
      jobId: leg.jobId,
      label: leg.label,
      arriveMinutes: serviceStart,
      departMinutes,
      travelMinutes,
      waitMinutes,
    });

    prevEndLoc = leg.dropoff;
    prevEndTime = departMinutes;
    hasPriorLeg = true;
  }

  const uniqueIssues = dedupeIssues(issues);
  return {
    ok: uniqueIssues.length === 0,
    errors: formatIssuesEnglish(uniqueIssues),
    issues: uniqueIssues,
    timeline,
    totalDeadheadKm,
  };
}

function dedupeIssues(issues: ScheduleValidationIssue[]): ScheduleValidationIssue[] {
  const seen = new Set<string>();
  const out: ScheduleValidationIssue[] = [];
  for (const issue of issues) {
    const key = `${issue.code}:${JSON.stringify(issue.params)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(issue);
  }
  return out;
}
